import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AvatarGroup from "@mui/material/AvatarGroup";
import { Chip, Collapse, Pagination, Stack, TablePagination, TableSortLabel, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import ThemeFilterCard from "../ThemeFilter/ThemeFilterCard";
import "./Themetable.css";
import { Box } from "@mui/system";
import AdditionalContentTheme from "./AdditionalContentTheme";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import DuplicateThemeModal from "../ThemeModal/DuplicateThemeModal";
import IconButton from "@mui/material/IconButton";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { selectUserData } from "../../../../../features/userSlice";
import { useSelector } from "react-redux";
import { CheckPermissionsDesign } from "../../../../../pages/AssetManagement/Design/CheckPermission/CheckDesignPermissions";
import { useDispatch } from "react-redux";
import {
  setPage,
  setRowsPerPage,
  setSelectedThemeShareuid,
  setSort,
} from "../../../../../features/themeSlice";
import { formatDate } from '../../../../../utils/helpers';

function ThemeTable({ tableHeadings }: any) {
  const {selectedTheme,selectedFeatures,selectedGroups,sort,page,rowsPerPage,designThemesData} = useSelector((state: any) => state?.theme);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openRow, setOpenRow] = React.useState<number | null>(null);
  const [openModal, setOpenModal] = useState(false);

  const userDate = useSelector(selectUserData);
  const hasEditThemePermissions = CheckPermissionsDesign(
    userDate?.data?.products,
    "edit_theme"
  );
  const hasDuplicateThemePermissions = CheckPermissionsDesign(
    userDate?.data?.products,
    "duplicate_theme"
  );
  const hasDeleteThemePermissions = CheckPermissionsDesign(
    userDate?.data?.products,
    "delete_theme"
  );

  const modifiedDesignThemeData =
    designThemesData?.length > 0
    ? designThemesData.map((item: any) => {
      const labels = item?.templates?.flatMap(
        (template: any) => template?.labels || []
      );
      const uniqueLabels = labels.filter(
        (label: any, index: any) => labels.indexOf(label) === index
      );

      return {
        shared_uid: item.shared_uid,
        name: item.name,
        dictionary: item.dictionary,
        groups_appearance: item.groups_appearance,
        example_page: item.example_page,
        modified: item.modified,
        labels: uniqueLabels, // Use the unique labels array
        templates: item.templates.map((template: any) => ({
          shared_uid: template.shared_uid,
          component: template.component,
          name: template.name,
          labels: uniqueLabels,
        })),
      };
    })
    : [];

  const filteredData = modifiedDesignThemeData?.filter((designThemes: any) => {
    const selectedThemeName =
      selectedTheme && selectedTheme.length !== 0
        ? selectedTheme.includes(designThemes?.name)
        : true;

    const selectedThemeGroupsName =
      selectedGroups && selectedGroups.length !== 0
        ? selectedGroups.some((group: any) =>
            designThemes?.groups_appearance.includes(group)
          )
        : true;

    const selectedThemeFeaturesName =
      selectedFeatures && selectedFeatures.length !== 0
        ? selectedFeatures.some((feature: any) =>
            designThemes?.labels.includes(feature)
          )
        : true;

    return (
      selectedThemeName && selectedThemeGroupsName && selectedThemeFeaturesName
    );
  });

  //-- Sorting Data
  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        let aValue = a[keytoSort];
        let bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        // Special case: if sorting by "modified", compare as Date objects
        if (keytoSort === "modified") {
          const dateA = new Date(aValue);
          const dateB = new Date(bValue);
          return sort.direction === "asc"
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  const tableheaders = [
    { id: 0, KEY: "name", label: "Name", sortable: true },
    { id: 1, KEY: "groups_appearance", label: "Groups", sortable: true },
    { id: 2, KEY: "modified", label: "Last Updated", sortable: true },
    { id: 3, KEY: "labels", label: "Features", sortable: true },
    { id: 4, KEY: "quick_actions", label: "Quick Actions", sortable: false },
    { id: 5, KEY: "more_info", label: "More Info", sortable: false },
  ];

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    dispatch(
      setSort({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  // -- Function to handle pagination
  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  //-- Function to handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
  };

  //-- Function to handle click on edit button
  const handleClickOnEdit = (row: any) => {
    dispatch(setSelectedThemeShareuid(row?.shared_uid));
    const params = new URLSearchParams(location.search);
    params.set("themeSharedUid", row?.shared_uid); // Add new param while keeping others
    params.set('page', '0');
    navigate(`edit_new_theme?${params.toString()}`);
  };

  const handleRowClick = (row: any) => {
    setOpenRow(openRow === row.shared_uid ? null : row.shared_uid);
    dispatch(setSelectedThemeShareuid(row.shared_uid)); // Use the row's unique identifier
  };

  const handleOpenModal = (shared_uid: any) => {
    dispatch(setSelectedThemeShareuid(shared_uid)); // Use the row's unique identifier
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const sortedRows = getSortedData(filteredData || []);
  const paginatedThemes = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
    // eslint-disable-next-line
  }, [searchParams]);

  useEffect(() => {
    // Get current page from URL searchParams if it exists
    const currentPage = searchParams.get("page");

    // If page exists in URL, use that value (convert to number)
    if (currentPage !== null) {
      dispatch(setPage(Number(currentPage)));
    }
    // Otherwise reset to page 0 (when filters change)
    else {
      dispatch(setPage(0));
      // Also update URL to reflect page 0
      setSearchParams((prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);
        updatedParams.set("page", "0");
        return updatedParams;
      });
    }
    // eslint-disable-next-line
  }, [searchParams, selectedTheme && page, selectedFeatures, selectedGroups]);

  return (
    <>
      <Paper>
        <ThemeFilterCard />
        <TableContainer
          className="themeWrap"
          component={Paper}
          sx={{ marginTop: "16px" }}
        >
          <Table aria-label="collapsible table" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {tableheaders?.map((header: any) => (
                  <TableCell key={header.id}>
                    <TableSortLabel
                      active={sort.keytoSort === header.KEY}
                      direction={
                        sort.keytoSort === header.KEY ? sort.direction : "asc"
                      }
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedThemes?.length > 0 && paginatedThemes ? (
                paginatedThemes?.map((row: any, index: any) => (
                  <React.Fragment key={index}>
                    <TableRow
                      sx={{
                        "& > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      <TableCell align="left">{row.name}</TableCell>
                      <TableCell align="left">
                        <div className="chip-data">
                          {Array.isArray(row.groups_appearance) &&
                            row.groups_appearance?.length > 0 ? (
                            <>
                              {/* Display the first group */}
                              <Chip
                                label={row.groups_appearance[0]}
                                sx={{
                                  backgroundColor: "#7B1FA2",
                                  color: "#ffffff",
                                  height: "26px",
                                }}
                              />
                              {/* Display the second group if it exists */}
                              {row.groups_appearance?.length > 1 && (
                                <Chip
                                  label={row.groups_appearance[1]}
                                  sx={{
                                    backgroundColor: "#7B1FA2",
                                    color: "#ffffff",
                                    height: "26px",
                                  }}
                                />
                              )}

                              {/* Check if there are additional related groups and render AvatarGroup */}
                              {row.groups_appearance?.length > 2 && (
                                <AvatarGroup
                                  total={row.groups_appearance?.length - 2} // Show the number of additional groups
                                  variant="circular"
                                />
                              )}
                            </>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell align="left">
                        {formatDate(row.modified)}
                      </TableCell>
                      <TableCell align="left">
                        <div className="chip-data">
                          {Array.isArray(row.templates[0]?.labels) &&
                            row.templates[0]?.labels?.length > 0 ? (
                            <>
                              {/* Display the first group */}
                              <Chip
                                label={row.templates[0]?.labels[0]}
                                sx={{
                                  backgroundColor: "#7B1FA2",
                                  color: "#ffffff",
                                  height: "26px",
                                }}
                              />
                              {/* Display the second group if it exists */}
                              {row.templates[0]?.labels?.length > 1 && (
                                <Chip
                                  label={row.templates[0]?.labels[1]}
                                  sx={{
                                    backgroundColor: "#7B1FA2",
                                    color: "#ffffff",
                                    height: "26px",
                                  }}
                                />
                              )}

                              {/* Check if there are additional related groups and render AvatarGroup */}
                              {row.templates[0]?.labels?.length > 2 && (
                                <AvatarGroup
                                  total={row.labels?.length - 2} // Show the number of additional groups
                                  variant="circular"
                                />
                              )}
                            </>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell align="left">
                        <>
                          <Box display="flex" gap={1}>
                            <Tooltip title="Edit" placement="top">
                              <Box>
                                {hasEditThemePermissions ? (
                                  <IconButton
                                    data-tracker="144"
                                    size="small"
                                    onClick={() => handleClickOnEdit(row)}
                                  >
                                    <CreateOutlinedIcon
                                      fontSize="small"
                                      color="secondary"
                                    />
                                  </IconButton>
                                ) : (
                                  <IconButton
                                    size="small"
                                    disabled
                                    color="primary"
                                  >
                                    <CreateOutlinedIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </Tooltip>
                            <Tooltip title="Duplicate" placement="top">
                              {hasDuplicateThemePermissions ? (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleOpenModal(row?.shared_uid)
                                  }
                                >
                                  <ContentCopyOutlinedIcon
                                    data-tracker="142"
                                    fontSize="small"
                                    color="secondary"
                                  />
                                </IconButton>
                              ) : (
                                <IconButton
                                  size="small"
                                  disabled
                                  color="primary"
                                >
                                  <ContentCopyOutlinedIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Tooltip>
                            <Tooltip title="Delete" placement="top">
                              {hasDeleteThemePermissions ? (
                                <IconButton
                                  size="small"
                                  disabled
                                  color="primary"
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                <IconButton
                                  size="small"
                                  disabled
                                  color="primary"
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Tooltip>
                          </Box>
                        </>
                      </TableCell>
                      <TableCell
                        style={{
                          paddingBottom: 0,
                          paddingTop: 0,
                          border: "none",
                        }}
                        colSpan={Object.keys(tableHeadings).length + 1}

                      >
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          color="secondary"
                          onClick={() => handleRowClick(row)}
                        >
                          {openRow === row.shared_uid ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon data-tracker="137" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{
                          paddingBottom: 0,
                          paddingTop: 0,
                          border: "none",
                        }}
                        colSpan={Object.keys(tableHeadings).length + 1}
                      >
                        <Collapse
                          in={openRow === row.shared_uid}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ padding: "20px 0px" }}>
                            <AdditionalContentTheme />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow
                  sx={{
                    "& > *": {
                      borderBottom:
                        "1px solid rgba(224, 224, 224, 1) !important",
                    },
                  }}
                >
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1">No Data Available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={filteredData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                  {
                    display: "none",
                  },
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                ".MuiTablePagination-toolbar": {
                  paddingLeft: "16px",
                },
              }}
            />
            <Pagination
              count={Math.ceil(filteredData?.length / rowsPerPage)}
              showFirstButton
              showLastButton
              className="pagination-button"
              color="secondary"
              size="small"
              sx={{
                ".css-wjh20t-MuiPagination-ul": {
                  justifyContent: "center",
                },
              }}
              onChange={(_, value) => handleChangePage(null, value - 1)}
              page={page + 1}
            />
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={filteredData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              classes={{
                displayedRows: "hidden-displayed-rows", // Apply the custom class
              }}
              sx={{
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                ".MuiTablePagination-input": {
                  marginRight: "15px",
                },
              }}
            />
          </Stack>
          <DuplicateThemeModal open={openModal} onClose={handleCloseModal} />
        </TableContainer>
      </Paper>
    </>
  );
}
export default ThemeTable;
